import { useState } from "react";

import { useAlertRules } from "../api/hooks/useAlertRules";
import { useDeleteAlertRule } from "../api/hooks/useDeleteAlertRule";
import { useRestoreAlertRule } from "../api/hooks/useRestoreAlertRule";
import type { components } from "../api/schema";
import { AlertRuleBuilderDialog } from "../components/alerts/AlertRuleBuilderDialog";

type AlertRuleResponse = components["schemas"]["AlertRuleResponse"];

function formatDate(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return `${dt.toISOString().slice(0, 10)} ${dt.toISOString().slice(11, 16)}Z`;
}

function RuleRow({
  rule,
  onDelete,
  onRestore,
  isMutating,
}: {
  rule: AlertRuleResponse;
  onDelete: (rule: AlertRuleResponse) => void;
  onRestore: (rule: AlertRuleResponse) => void;
  isMutating: boolean;
}) {
  return (
    <article
      data-testid={`alert-rule-row-${rule.alert_rule_id}`}
      data-deleted={rule.is_deleted ? "true" : undefined}
      className={
        "rounded-control border border-outline-variant bg-surface-container p-4 " +
        (rule.is_deleted ? "opacity-60" : "")
      }
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-display text-base font-medium text-on-surface">
            {rule.name}
          </span>
          <span
            className={
              "rounded px-2 py-0.5 text-xs " +
              (rule.active
                ? "bg-primary text-on-primary"
                : "bg-surface-container-high text-on-surface-variant")
            }
          >
            {rule.active ? "active" : "paused"}
          </span>
          {rule.is_deleted ? (
            <span className="rounded bg-surface-container-high px-2 py-0.5 text-xs text-on-surface-variant">
              deleted
            </span>
          ) : null}
        </div>
        <span className="text-xs text-on-surface-variant">
          Updated {formatDate(rule.updated_at)}
        </span>
      </header>
      <pre className="mt-2 overflow-x-auto rounded bg-surface-container-high p-2 text-xs text-on-surface-variant">
        {JSON.stringify(rule.criteria, null, 2)}
      </pre>
      <footer className="mt-3 flex justify-end gap-2">
        {rule.is_deleted ? (
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onRestore(rule)}
            className="rounded-control border border-outline-variant bg-surface px-3 py-1 text-sm text-on-surface transition-colors hover:bg-surface-container-high"
          >
            Restore
          </button>
        ) : (
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onDelete(rule)}
            className="rounded-control border border-outline-variant bg-surface px-3 py-1 text-sm text-danger transition-colors hover:bg-danger-soft"
          >
            Delete
          </button>
        )}
      </footer>
    </article>
  );
}

export function AlertRulesPage() {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const rulesQuery = useAlertRules({ include_deleted: includeDeleted });
  const deleteMutation = useDeleteAlertRule();
  const restoreMutation = useRestoreAlertRule();

  const items = rulesQuery.data?.items ?? [];
  const busy = deleteMutation.isPending || restoreMutation.isPending;

  return (
    <section className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Alert rules
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Rules fire once per matching trade close. Acknowledge each
            firing in the Alerts inbox.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-on-surface-variant">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(event) => setIncludeDeleted(event.target.checked)}
            />
            Show deleted
          </label>
          <button
            type="button"
            onClick={() => setBuilderOpen(true)}
            className="rounded-control bg-primary px-3 py-1.5 text-sm font-medium text-on-primary transition-colors hover:bg-primary-hover"
          >
            + New rule
          </button>
        </div>
      </header>

      {rulesQuery.isLoading ? (
        <p role="status" aria-live="polite" className="text-on-surface-variant">
          Loading rules…
        </p>
      ) : rulesQuery.isError ? (
        <p role="alert" className="text-sm font-medium text-danger">
          Failed to load alert rules.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-on-surface-variant">
          {includeDeleted
            ? "No rules in the trash."
            : "No alert rules yet. Create one to start receiving inbox alerts."}
        </p>
      ) : (
        <ul data-testid="alert-rules-list" className="space-y-3">
          {items.map((rule) => (
            <li key={rule.alert_rule_id}>
              <RuleRow
                rule={rule}
                isMutating={busy}
                onDelete={(target) => deleteMutation.mutate(target.alert_rule_id)}
                onRestore={(target) =>
                  restoreMutation.mutate(target.alert_rule_id)
                }
              />
            </li>
          ))}
        </ul>
      )}

      <AlertRuleBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
      />
    </section>
  );
}
